import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Typography,
  Grid,
  Box,
  Paper,
  Snackbar,
  Alert
} from '@mui/material';
import {
  createTheme,
  ThemeProvider,
  styled,
  useTheme
} from '@mui/material/styles';
import { ChartScatter as StatsIcon, HelpCircle as HelpIcon } from 'lucide-react';

// Importa tu servicio de stats
import statsService from '../services/AGTUps-product-services/statsService';

/** =========================================================================
 *  1) Paleta de colores y theme personalizado
 * ========================================================================= */
const COLORS = {
  primary: 'rgba(128, 128, 128, 0.5)',
  text: {
    light: '#FFFFFF',
    dark: '#FFFFFF'
  }
};

const createCustomTheme = (mode, actualMode) =>
  createTheme({
    palette: { mode, actualMode },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: '12px',
            textTransform: 'none',
            boxShadow: 'none',
            '&:hover': { boxShadow: 'none' }
          },
          contained: {
            backgroundColor: actualMode === 'wallpaper' ? '#FFFFFF' : COLORS.primary,
            color: actualMode === 'wallpaper' ? '#000000' : '#ffffff',
            '&:hover': {
              backgroundColor: actualMode === 'wallpaper' ? 'rgba(255, 255, 255, 0.85)' : COLORS.primary
            }
          },
          text: {
            color: actualMode === 'wallpaper' ? '#FFFFFF' : COLORS.text[mode]
          }
        }
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: '16px',
            border: '1px solid',
            borderColor: actualMode === 'wallpaper'
              ? 'rgba(128, 128, 128, 0.3)'
              : (mode === 'light'
                  ? 'rgba(0, 0, 0, 0.12)'
                  : 'rgba(255, 255, 255, 0.12)'),
            backgroundColor: actualMode === 'wallpaper'
              ? 'rgba(128, 128, 128, 0.3)'
              : undefined,
            backdropFilter: actualMode === 'wallpaper' ? 'blur(10px)' : 'none',
            WebkitBackdropFilter: actualMode === 'wallpaper' ? 'blur(10px)' : 'none'
          }
        }
      }
    }
  });

/** =========================================================================
 *  2) Estilos “Apple-inspired” para la sección Stats
 * ========================================================================= */
const getAppleColors = (theme) => ({
  background:
    theme.palette.actualMode === 'wallpaper'
      ? 'rgba(128, 128, 128, 0.3)'
      : (theme.palette.mode === 'dark'
          ? 'rgba(28, 28, 30, 0.95)'
          : 'rgba(255, 255, 255, 0.95)'),
  textPrimary:
    theme.palette.actualMode === 'wallpaper'
      ? '#FFFFFF'
      : (theme.palette.mode === 'dark'
          ? '#FFFFFF'
          : '#1D1D1F'),
  textSecondary:
    theme.palette.actualMode === 'wallpaper'
      ? 'rgba(255, 255, 255, 0.7)'
      : (theme.palette.mode === 'dark'
          ? 'rgba(255, 255, 255, 0.7)'
          : 'rgba(29, 29, 31, 0.7)')
});

const appleGradients = {
  publishedProducts: {
    light: 'linear-gradient(145deg, #F5F5F7, #FFFFFF)',
    dark: 'linear-gradient(145deg, #1D1D1F, #2C2C2E)',
    wallpaper: 'rgba(128, 128, 128, 0.25)'
  },
  activeNegotiations: {
    light: 'linear-gradient(145deg, #F5F5F7, #FFFFFF)',
    dark: 'linear-gradient(145deg, #1D1D1F, #2C2C2E)',
    wallpaper: 'rgba(128, 128, 128, 0.25)'
  },
  visualizations: {
    light: 'linear-gradient(145deg, #F5F5F7, #FFFFFF)',
    dark: 'linear-gradient(145deg, #1D1D1F, #2C2C2E)',
    wallpaper: 'rgba(128, 128, 128, 0.25)'
  },
  searches: {
    light: 'linear-gradient(145deg, #F5F5F7, #FFFFFF)',
    dark: 'linear-gradient(145deg, #1D1D1F, #2C2C2E)',
    wallpaper: 'rgba(128, 128, 128, 0.25)'
  },
  customerSatisfaction: {
    light: 'linear-gradient(145deg, #F5F5F7, #FFFFFF)',
    dark: 'linear-gradient(145deg, #1D1D1F, #2C2C2E)',
    wallpaper: 'rgba(128, 128, 128, 0.25)'
  },
  marketplaceRanking: {
    light: 'linear-gradient(145deg, #F5F5F7, #FFFFFF)',
    dark: 'linear-gradient(145deg, #1D1D1F, #2C2C2E)',
    wallpaper: 'rgba(128, 128, 128, 0.25)'
  }
};

const StatsWrapper = styled(Box)(({ theme }) => ({
  width: '100%',
  padding: theme.spacing(2),
  maxHeight: '65vh',
  overflowY: 'auto',
  backgroundColor: getAppleColors(theme).background,
  backdropFilter: 'blur(20px)',
  WebkitBackdropFilter: 'blur(20px)',
  borderRadius: '12px'
}));

const StatsInnerPaper = styled(Paper)(({ theme }) => ({
  borderRadius: theme.spacing(3),
  padding: theme.spacing(3),
  background: 'transparent',
  boxShadow: 'none',
  color: getAppleColors(theme).textPrimary
}));

const StatBox = styled(Box)(({ theme, bgKey }) => {
  const actualMode = theme.palette.actualMode;
  const mode = theme.palette.mode;
  const gradient = actualMode === 'wallpaper'
    ? appleGradients[bgKey]?.wallpaper
    : appleGradients[bgKey]?.[mode === 'dark' ? 'dark' : 'light'];

  return {
    borderRadius: theme.spacing(2.5),
    padding: theme.spacing(3),
    background: gradient,
    backdropFilter: actualMode === 'wallpaper' ? 'blur(8px)' : 'none',
    WebkitBackdropFilter: actualMode === 'wallpaper' ? 'blur(8px)' : 'none',
    border: actualMode === 'wallpaper' ? '1px solid rgba(128, 128, 128, 0.3)' : 'none',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between'
  };
});

const LoadingOverlay = styled(Box)(() => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: '200px',
  width: '100%'
}));

/* =========================================================================
   3) Componente interno con la lógica principal
   =========================================================================*/
function StatsShortcutContent({ open, onClose }) {
  // useTheme() retorna el "customTheme" (ya dentro del ThemeProvider)
  const theme = useTheme();

  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [error, setError] = useState(null);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info'
  });

  // Cargar stats al abrir
  useEffect(() => {
    if (!open) return;

    const fetchStats = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await statsService.getAllStats();
        if (response.success) {
          const formatted = statsService.formatStats(response.data);
          setStats(formatted);
        } else {
          throw new Error(response.error || 'Error fetching stats');
        }
      } catch (err) {
        console.error('Error loading stats:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();

    // Suscripción a actualizaciones en tiempo real (si tu servicio lo soporta)
    const unsubscribe = statsService.subscribeToUpdates((newStats) => {
      if (newStats.success) {
        const formattedStats = statsService.formatStats(newStats.data);
        setStats(formattedStats);
      }
    });

    return () => {
      // cleanup: anular suscripción
      unsubscribe();
    };
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      aria-labelledby="stats-shortcut-dialog-title"
    >
      <DialogTitle
        id="stats-shortcut-dialog-title"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <StatsIcon />
          <Typography variant="h6">Marketplace Stats</Typography>
        </Box>
        <HelpIcon size={20} />
      </DialogTitle>

      <DialogContent dividers>
        <StatsWrapper>
          {loading ? (
            <LoadingOverlay>
              <CircularProgress />
            </LoadingOverlay>
          ) : error ? (
            <Typography color="error" align="center">
              Error loading stats: {error}
            </Typography>
          ) : (
            <StatsInnerPaper>
              <Typography
                variant="h4"
                sx={{ mb: 1, fontWeight: 600, fontSize: '1.6rem' }}
              >
                Statistics
              </Typography>
              <Typography
                variant="body2"
                sx={{ mb: 3, color: getAppleColors(theme).textSecondary }}
              >
                Overview of your business metrics
              </Typography>

              <Grid container spacing={3}>
                {stats &&
                  Object.entries(stats).map(([key, stat], idx) => (
                    <Grid item xs={12} sm={6} md={4} key={idx}>
                      <StatBox bgKey={key}>
                        <Typography
                          variant="subtitle2"
                          sx={{
                            fontWeight: 500,
                            color: getAppleColors(theme).textSecondary,
                            mb: 0.5
                          }}
                        >
                          {stat.label}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ color: getAppleColors(theme).textSecondary, opacity: 0.8 }}
                        >
                          {stat.description}
                        </Typography>
                        <Box sx={{ mt: 2, display: 'flex', alignItems: 'baseline' }}>
                          <Typography
                            variant="h5"
                            sx={{ fontWeight: 600, letterSpacing: '-0.5px' }}
                          >
                            {typeof stat.value === 'number'
                              ? statsService.formatNumber(stat.value)
                              : stat.value}
                          </Typography>
                          {stat.unit && (
                            <Typography
                              variant="body1"
                              sx={{
                                ml: 1,
                                color: getAppleColors(theme).textSecondary
                              }}
                            >
                              {stat.unit}
                            </Typography>
                          )}
                        </Box>
                      </StatBox>
                    </Grid>
                  ))}
              </Grid>
            </StatsInnerPaper>
          )}
        </StatsWrapper>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>
          Close
        </Button>
      </DialogActions>

      {/* Snackbar para mensajes informativos */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Dialog>
  );
}

/* =========================================================================
   4) Componente principal (export default):
      - Llama a useTheme() para obtener modo del theme padre.
      - Crea el customTheme.
      - Monta el <StatsShortcutContent> dentro de <ThemeProvider>.
   =========================================================================*/
export default function StatsShortcut({ open, onClose }) {
  // 1) Obtenemos el theme padre
  const parentTheme = useTheme();
  // 2) Creamos el customTheme según el modo
  const customTheme = createCustomTheme(parentTheme.palette.mode, parentTheme.palette.actualMode);

  return (
    <ThemeProvider theme={customTheme}>
      <StatsShortcutContent open={open} onClose={onClose} />
    </ThemeProvider>
  );
}
